/**
 * Helper class for currency-related operations
 */
export class CurrencyHelper {
  private static formatterCache = new Map<string, Intl.NumberFormat>();

  /**
   * Format a number as currency using the store's default currency.
   * @param value - The number to format
   * @returns Formatted currency string
   */
  public static formatCurrency(value: any): string {
    if (value === null || value === undefined || value === 'null') { return '-'; }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) { return '-'; }
    return this.formatCurrencyWithCode(numValue, this.getStoreCurrency());
  }

  /**
   * Get currency symbol from currency code
   * @param currencyCode - The currency code (e.g., 'USD', 'EUR')
   * @returns Currency symbol
   */
  public static getCurrencySymbol(currencyCode: string = 'USD'): string {
    try {
      const symbol = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      })
      .format(0)
      .replace(/[0-9.,\s]/g, '');

      return symbol;
    } catch (error) {
      console.error('Error getting currency symbol:', error);
      return currencyCode;
    }
  }

  /**
   * Format a number as currency with a specific symbol
   * @param value - The number to format
   * @param currencyCode - The currency code to use for formatting
   * @returns Formatted currency string with symbol
   */
  public static formatWithSymbol(value: any, currencyCode: string): string {
    if (!value || value === 'null') { return '-'; }

    try {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) { return '-'; }

      const symbol = this.getCurrencySymbol(currencyCode);
      const decimals = this.getDecimalPlaces(currencyCode);
      return `${symbol}${numValue.toFixed(decimals)}`;
    } catch (error) {
      console.error('Error formatting currency with symbol:', error);
      return '-';
    }
  }

  /**
   * Format a number as currency without symbol
   * @param value - The number to format
   * @returns Formatted number with 2 decimal places
   */
  public static formatNumberOnly(value: any): string {
    if (!value || value === 'null') { return '0.00'; }

    try {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) { return '0.00'; }

      return numValue.toFixed(2);
    } catch (error) {
      console.error('Error formatting number:', error);
      return '0.00';
    }
  }

  /**
   * Format a number with comma separators (rounded to integer)
   * @param value - The number to format
   * @returns Formatted number string with comma separators
   */
  public static formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) { return '0'; }
    if (Number.isNaN(value)) { return '0'; }
    return Math.round(value).toLocaleString();
  }

  /**
   * Format a number as percentage
   * @param value - The number to format (e.g., 45.5 for 45.5%)
   * @returns Formatted percentage string
   */
  public static formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined) { return '0%'; }
    if (Number.isNaN(value)) { return '0%'; }
    return `${value.toFixed(1)}%`;
  }

  /**
   * Get color for units sold based on value thresholds
   * @param units - Number of units sold
   * @returns Vuetify color name
   */
  public static getUnitsColor(units: number): string {
    if (!Number.isFinite(units)) { return 'grey'; }
    if (units === 0) { return 'grey'; }
    if (units < 10) { return 'orange'; }
    if (units < 50) { return 'blue'; }
    return 'primary';
  }

  private static readonly ZERO_DECIMAL_CURRENCIES = [
    'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW',
    'PYG', 'RWF', 'UGX', 'UYI', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'
  ];

  private static readonly THREE_DECIMAL_CURRENCIES = ['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND'];

  public static getStoreCurrency(): string {
    return localStorage.getItem('currency') || 'USD';
  }

  public static getDecimalPlaces(code: string): number {
    if (!code) return 2;
    const upper = code.toUpperCase();
    if (this.ZERO_DECIMAL_CURRENCIES.includes(upper)) return 0;
    if (this.THREE_DECIMAL_CURRENCIES.includes(upper)) return 3;
    return 2;
  }

  public static getCurrencyStep(currencyCode: string): string {
    const decimals = this.getDecimalPlaces(currencyCode);
    if (decimals === 0) return '1';
    return `0.${'0'.repeat(decimals - 1)}1`;
  }

  public static roundForCurrency(value: number, currencyCode: string): number {
    const decimals = this.getDecimalPlaces(currencyCode);
    const factor = Math.pow(10, decimals);
    const roundedAbs = Math.round((Math.abs(value) + Number.EPSILON) * factor) / factor;
    return value < 0 ? -roundedAbs : roundedAbs;
  }

  public static formatCurrencyWithCode(value: number | null | undefined, currencyCode: string): string {
    if (value === null || value === undefined || isNaN(value)) return '-';
    const decimals = this.getDecimalPlaces(currencyCode);
    const cacheKey = `${currencyCode}:${decimals}`;
    let formatter = this.formatterCache.get(cacheKey);
    if (!formatter) {
      try {
        formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });
        this.formatterCache.set(cacheKey, formatter);
      } catch {
        return `${currencyCode} ${value.toFixed(decimals)}`;
      }
    }
    return formatter.format(value);
  }

  /**
   * Get color for margin percentage based on value thresholds
   * @param margin - Margin percentage
   * @returns Vuetify color name
   */
  public static getMarginColor(margin: number): string {
    if (!Number.isFinite(margin)) { return 'grey'; }
    if (margin < 0) { return 'error'; }
    if (margin < 20) { return 'warning'; }
    if (margin < 50) { return 'info'; }
    return 'success';
  }
}
